import { apiClient } from './client';

/**
 * Cohort report (COHORT-REPORT): the participants of one procedure side by
 * side, one row per person, each cell a percentile among the people who did
 * that procedure. Only metrics visible for the procedure are included.
 */

/** How a column's cells are coloured.
 *  - `peer`: better/worse than the peers (the scorecard's direction).
 *  - `adjusted`: like `peer`, ranked with the effect of case length removed.
 *  - `neutral`: shaded by percentile with no verdict (duration, EEG). */
export type CohortColour = 'peer' | 'adjusted' | 'neutral';

export interface CohortColumn {
  key: string;
  modality: 'eeg' | 'hand' | 'both';
  direction: 'higher' | 'lower' | 'none';
  colour: CohortColour;
  unit: string | null;
}

export interface CohortCoverage {
  session_rows: number;
  empty: number;
  short: number;
  used: number;
  people: number;
  no_eeg: number;
  no_hand: number;
}

export interface CohortRow {
  participant_user_id: string;
  name: string;
  sessions: number;
  values: Record<string, number | null>;
  /** 0–100, oriented so higher = better for peer/adjusted columns. */
  percentiles: Record<string, number | null>;
}

export interface CohortProcedure {
  procedure_id: string;
  name: string;
  columns: CohortColumn[];
  coverage: CohortCoverage;
  rows: CohortRow[];
}

export interface WithinPersonEeg {
  participant_user_id: string;
  name: string;
  /** procedure id → metric key → deviation from the person's own average. */
  stations: Record<string, Record<string, number>>;
}

export interface CohortReportRequest {
  procedure_ids: string[];
  date_from?: string | null;
  date_to?: string | null;
  role_name?: string | null;
}

export interface CohortReport {
  generated_at: string;
  request: CohortReportRequest;
  min_session_seconds: number;
  procedures: CohortProcedure[];
  within_person_eeg: WithinPersonEeg[];
  within_person_keys: string[];
}

export async function runCohortReport(
  tenantId: string,
  body: CohortReportRequest,
): Promise<CohortReport> {
  const { data } = await apiClient.post<CohortReport>(
    `/api/v1/tenants/${tenantId}/cohort-report`,
    body,
  );
  return data;
}
